/**
 * ProfileMenu — the shell identity menu, grown from Phase 2's `UserMenu`
 * (avatar + name + "Log out") into the Phase 2.5b profile surface: the
 * signed-in user's photo, and the self-service upload/remove for it.
 *
 * ===========================================================================
 * WHERE THIS LIVES, AND WHY IT IS THE "TOPBAR AVATAR"
 * ===========================================================================
 * This shell draws its identity block in the SIDEBAR's 56px UserBlock (Figma
 * `Shell/Sidebar` 12:908) — the topbar's right group is search/bell/theme/
 * "Log Out" and contains no avatar in the design. The brief's "visible in
 * topbar" maps to this block: it is the one persistent avatar of the signed-in
 * user in the chrome, at topbar height. Adding a SECOND identity disc to the
 * `TopBar` component would mean two profile menus or a disc that opens
 * nothing, both worse than the design it would decorate. Recorded in the
 * report rather than silently reinterpreted.
 *
 * ===========================================================================
 * THE MENU STAYS OPEN THROUGH AN UPLOAD, ON PURPOSE
 * ===========================================================================
 * "Upload photo" and "Remove photo" are menu items that `preventDefault()`
 * their select: the menu is the only surface that can show the preview, the
 * "Uploading…" state and a refusal (wrong type, too big, the server's
 * magic-byte 422, the rate limiter's 429) — closing it on click would throw
 * that context away and leave the user staring at an unchanged disc wondering
 * whether anything happened. "Log out" keeps the default select-and-close.
 *
 * The PREVIEW is the picked file as a local object URL, shown immediately in
 * the header disc (and the trigger disc) while the upload runs. On success the
 * mutation AWAITS the avatar query's refetch (`useOwnAvatarMutations`), so by
 * the time the preview is dropped the real re-encoded photo is already the
 * query's answer — the disc never flashes back to initials. On refusal the
 * preview is dropped and the initials/old photo return: showing a photo the
 * server refused would be the UI lying about state.
 *
 * Validation mirrors the server (`avatar-file.ts`) but the server stays the
 * authority — a file that lies about its MIME type gets past the mirror and is
 * refused by the magic-byte sniff; that refusal renders here too, via the
 * thrown `ApiRequestError`'s own message.
 *
 * ===========================================================================
 * WEB TIER A · WT7 — ONE ITEM ADDED, AND THE WIRING IS OTHERWISE UNTOUCHED
 * ===========================================================================
 * `settings.html`: Settings is NOT a nav destination — `domain/nav.ts` has no
 * item for it and gains none — and "the page is reached from the identity
 * block at the top of the sidebar, which is where the shell already puts a
 * caret". That identity block is this menu, so this menu gets the way in: one
 * `Settings` item above the separator, a `Link` (so it is a real anchor with a
 * real href — middle-click and copy-link both work), pointing at the current
 * country's `settings` route.
 *
 * The MENU ITSELF is not migrated to the vendored `Menu` composition. Plan §3's
 * adoption rule is that an existing Radix wiring moves only when a Tier A
 * cluster rewrites its file, and adding one item is not rewriting it; the
 * migration (and the `data-[disabled]:text-fg-muted` 2.63:1 dim the vendored
 * `MenuItem` fixes) stays FILED as ledger item 1 row 6.
 *
 * THE HREF IS DERIVED, AND ITS ABSENCE IS HONEST. The route lives under
 * `/countries/:cc`, so the item needs a country. `useParams` rather than
 * `useCountryCode` deliberately: the hook THROWS off a country route, and this
 * menu is chrome — if a future surface renders it outside `RequireCountry`
 * there is genuinely no settings page to point at, and the honest answer is to
 * omit the item rather than to crash the shell or to link somewhere invented.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Link, useParams } from 'react-router-dom';
import { ChevronDown, ImageUp, ImageOff, LogOut, Settings } from 'lucide-react';
import { isCountryCode } from '../../domain/countries';
import { useSession } from '../../auth/session';
import { ApiRequestError, ApiTransportError } from '../../lib/api';
import { Avatar } from '../ui';
import { useAvatarUrl, useOwnAvatarMutations } from '../../features/avatars/use-avatar';
import { AVATAR_ACCEPT_ATTR, validateAvatarFile } from '../../features/avatars/avatar-file';

const ITEM_CLASSES =
  'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-body13 text-fg outline-none data-[highlighted]:bg-subtle';

export function ProfileMenu(): React.JSX.Element {
  const { me, signOut } = useSession();
  const name = me?.name ?? 'Signed in';
  const { cc } = useParams<{ cc: string }>();
  const settingsHref = isCountryCode(cc) ? `/countries/${cc}/settings` : null;

  const photoUrl = useAvatarUrl(me?.id);
  const { upload, remove } = useOwnAvatarMutations(me?.id);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  /* The preview object URL, revoked whenever it is replaced and on unmount —
   * an un-revoked blob URL pins the whole file in memory for the page's life. */
  const previewRef = useRef<string | null>(null);
  const [preview, setPreviewState] = useState<string | null>(null);
  const setPreview = useCallback((next: string | null): void => {
    if (previewRef.current !== null) URL.revokeObjectURL(previewRef.current);
    previewRef.current = next;
    setPreviewState(next);
  }, []);
  useEffect(
    () => () => {
      if (previewRef.current !== null) URL.revokeObjectURL(previewRef.current);
    },
    [],
  );

  const busy = upload.isPending || remove.isPending;
  const displaySrc = preview ?? photoUrl;

  const handleFile = (file: File): void => {
    const refusal = validateAvatarFile(file);
    if (refusal !== null) {
      setError(refusal);
      return;
    }
    setError(null);
    setPreview(URL.createObjectURL(file));
    void upload
      .mutateAsync(file)
      .then(() => {
        // The awaited invalidation means the refetched photo is already the
        // query's answer here — dropping the preview swaps like-for-like.
        setPreview(null);
      })
      .catch((cause: unknown) => {
        setPreview(null);
        setError(
          cause instanceof ApiRequestError || cause instanceof ApiTransportError
            ? cause.message
            : 'The photo could not be uploaded. Try again.',
        );
      });
  };

  const handleRemove = (): void => {
    setError(null);
    void remove
      .mutateAsync()
      .then(() => setPreview(null))
      .catch((cause: unknown) => {
        setError(
          cause instanceof ApiRequestError || cause instanceof ApiTransportError
            ? cause.message
            : 'The photo could not be removed. Try again.',
        );
      });
  };

  return (
    <DropdownMenu.Root>
      {/*
       * D2 · THE RAIL. Below `--breakpoint-shell` the sidebar is 64px wide and
       * this trigger is the avatar alone. Three rules and no state:
       *
       *  · the NAME goes `sr-only`, not `hidden` — the avatar is `decorative`,
       *    so the name text is the ONLY thing naming this button. Removing it
       *    from the accessibility tree would leave a nameless menu button in the
       *    chrome of every screen below 1440px.
       *  · the chevron goes `hidden`: it is `aria-hidden` already, so it has
       *    nothing to announce, and at 64px it is what pushes the avatar
       *    off-centre.
       *  · `w-full justify-between` becomes `w-auto justify-center`, because a
       *    full-width row with one 32px child in it is not centred.
       */}
      <DropdownMenu.Trigger className="flex w-full items-center justify-between gap-2 rounded-sm text-left max-shell:w-auto max-shell:justify-center">
        <span className="flex min-w-0 items-center gap-2 max-shell:gap-0">
          <Avatar name={name} userId={me?.id} src={displaySrc} size={32} decorative />
          <span className="truncate text-body font-medium text-fg max-shell:sr-only">{name}</span>
        </span>
        <ChevronDown
          aria-hidden="true"
          className="size-3.5 shrink-0 text-fg-muted max-shell:hidden"
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="start"
          sideOffset={4}
          className="z-50 w-[248px] rounded-md border border-line bg-surface p-1.5 shadow-[var(--crf-shadow-overlay)]"
        >
          {/* Identity header: who these actions apply to. */}
          <div className="flex items-center gap-2.5 px-2 pb-2 pt-1.5">
            <Avatar name={name} userId={me?.id} src={displaySrc} size={40} decorative />
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-body13 font-medium text-fg">{name}</span>
              {me === null ? null : (
                <span className="truncate text-label text-fg-secondary">{me.email}</span>
              )}
            </span>
          </div>

          {/* The picker itself. Hidden but INSIDE the content, so opening it
           * never moves focus outside the menu (which would dismiss it). */}
          <input
            ref={fileInputRef}
            type="file"
            accept={AVATAR_ACCEPT_ATTR}
            className="hidden"
            aria-hidden="true"
            tabIndex={-1}
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Same-file re-picks must re-fire change — a refused file that is
              // fixed externally and picked again is the expected retry path.
              event.target.value = '';
              if (file !== undefined) handleFile(file);
            }}
          />

          <DropdownMenu.Item
            disabled={busy}
            onSelect={(event) => {
              event.preventDefault();
              fileInputRef.current?.click();
            }}
            className={`${ITEM_CLASSES} data-[disabled]:cursor-not-allowed data-[disabled]:text-fg-muted`}
          >
            <ImageUp aria-hidden="true" className="size-3.5" />
            {photoUrl === undefined ? 'Upload photo' : 'Replace photo'}
          </DropdownMenu.Item>

          {photoUrl === undefined && preview === null ? null : (
            <DropdownMenu.Item
              disabled={busy}
              onSelect={(event) => {
                event.preventDefault();
                handleRemove();
              }}
              className={`${ITEM_CLASSES} data-[disabled]:cursor-not-allowed data-[disabled]:text-fg-muted`}
            >
              <ImageOff aria-hidden="true" className="size-3.5" />
              Remove photo
            </DropdownMenu.Item>
          )}

          {/* One live region for progress; refusals are asserted, not polite. */}
          {busy ? (
            <p aria-live="polite" className="px-2 py-1 text-label text-fg-secondary">
              {upload.isPending ? 'Uploading…' : 'Removing…'}
            </p>
          ) : null}
          {error !== null ? (
            /* On the danger TINT, not bare on the surface: `status/danger/text`
             * measures 2.98:1 against the dark surface (#ca2525 on #1f1f1f) —
             * an AA failure at 12px — while on its own mode-invariant tint it
             * is 4.52:1 in BOTH modes. Same pattern as the board's error strip. */
            <p
              role="alert"
              className="mx-0.5 my-1 rounded-sm bg-danger-bg px-2 py-1 text-label text-danger-fg"
            >
              {error}
            </p>
          ) : null}

          {settingsHref === null ? null : (
            <DropdownMenu.Item asChild>
              <Link to={settingsHref} className={ITEM_CLASSES}>
                <Settings aria-hidden="true" className="size-3.5" />
                Settings
              </Link>
            </DropdownMenu.Item>
          )}

          <DropdownMenu.Separator className="my-1.5 h-px bg-line" />
          <DropdownMenu.Item
            onSelect={() => {
              void signOut();
            }}
            className={ITEM_CLASSES}
          >
            <LogOut aria-hidden="true" className="size-3.5" />
            Log out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
